import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule } from '@openng/optimus-ui/table';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TagModule } from '@openng/optimus-ui/tag';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { FeedbackService } from '../../services/feedback.service';
import { UiStateService } from '../../services/ui-state.service';
import { FeedbackRow } from '../../models/feedback.types';
import { PeriodOption } from '../../models/dashboard.types';
import { DateRangeSelectorComponent, DateRange } from '../shared/date-range-selector.component';
import { ErrorBannerComponent } from '../shared/error-banner.component';

const PREVIEW_LENGTH = 80;

function containsHtml(value: string): boolean {
  return /<\/?[a-z][^>]*>/i.test(value);
}

/** Block-level boundaries that read as a space once the markup is gone. */
const BLOCK_BOUNDARY =
  /<br\b[^>]*>|<\/(?:p|div|li|ul|ol|h[1-6]|blockquote|pre|tr|td|th|table|section)\s*>/gi;

/** One parser for every row; `stripHtml` runs once per item on each recompute. */
let parser: DOMParser | undefined;

function stripHtml(value: string): string {
  if (!containsHtml(value)) return value;
  // textContent concatenates block elements with no separator, so "<p>a</p><p>b</p>" would come
  // back as "ab". Turn the boundaries into spaces first; inline tags stay glued on purpose.
  const spaced = value.replace(BLOCK_BOUNDARY, ' ');
  parser ??= new DOMParser();
  const body = parser.parseFromString(spaced, 'text/html').body;
  return (body.textContent || '').replace(/\s+/g, ' ').trim();
}

@Component({
  selector: 'app-feedback',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    TagModule,
    DialogModule,
    InputTextModule,
    DateRangeSelectorComponent,
    ErrorBannerComponent,
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
})
export class FeedbackComponent {
  protected feedbackService = inject(FeedbackService);
  protected uiState = inject(UiStateService);
  private router = inject(Router);

  protected selectedPeriod = this.uiState.selectedPeriod;
  protected startDate = this.uiState.startDate;
  protected endDate = this.uiState.endDate;

  protected showDetailModal = signal(false);
  protected selectedFeedback = signal<FeedbackRow | null>(null);
  protected search = signal('');

  /**
   * Feedback is submitted through a rich-text editor, so `content` is HTML.
   * Derive a plain-text form for the table preview and the search filter.
   */
  private feedbackRows = computed<FeedbackRow[]>(() =>
    this.feedbackService.feedbackList().map((item) => {
      const plainText = stripHtml(item.content);
      return {
        item,
        isHtml: containsHtml(item.content),
        plainText,
        preview:
          plainText.length > PREVIEW_LENGTH
            ? `${plainText.slice(0, PREVIEW_LENGTH)}...`
            : plainText,
      };
    }),
  );

  protected filteredFeedback = computed(() => {
    const term = this.search().trim().toLowerCase();
    const all = this.feedbackRows();
    if (!term) return all;
    return all.filter(
      (row) =>
        row.plainText.toLowerCase().includes(term) ||
        (row.item.user?.name || '').toLowerCase().includes(term) ||
        (row.item.user?.email || '').toLowerCase().includes(term) ||
        (row.item.organisation?.name || '').toLowerCase().includes(term),
    );
  });

  constructor() {
    effect(() => {
      this.uiState.startDate();
      this.uiState.endDate();
      untracked(() => this.loadData());
    });
  }

  loadData() {
    const { startDate, endDate } = this.uiState.getRange();
    this.feedbackService.loadFeedback(startDate, endDate);
  }

  onPeriodChange(period: PeriodOption) {
    this.uiState.setPeriod(period);
  }

  onDateRangeChange(dateRange: DateRange) {
    this.uiState.setRange(dateRange.startDate, dateRange.endDate);
  }

  openDetail(row: FeedbackRow) {
    this.selectedFeedback.set(row);
    this.showDetailModal.set(true);
  }

  openOrg(organisationId: number | null) {
    if (organisationId != null) {
      this.router.navigate(['/organizations', organisationId]);
    }
  }

  openUserSessions(userId: number | null) {
    if (userId != null) {
      this.uiState.setSessionUserFilter(userId);
      this.router.navigate(['/sessions']);
    }
  }
}
